import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { SeoService } from '../../core/services/seo.service';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { TooltipDirective } from '../../core/directives/tooltip.directive';
import { RouterLink } from '@angular/router';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';
import { TranslatePipe } from '@ngx-translate/core';

interface ProductSubItem {
  titleKey: string;
  descKey: string;
}

interface ProductShowcase {
  key: string;
  name: string;
  // Real brand-mark image when one exists; otherwise an ion-icon + text label
  // stands in until a designed logo is provided.
  logoSrc?: string;
  logoIcon?: string;
  tagKey: string;
  descKey: string;
  featureKeys: string[];
  comingSoonFeatureKeys?: string[];
  subItems?: ProductSubItem[];
  privacyKey?: string;
  privacyProduct?: string;
  ctaLabelKey: string;
  ctaRoute?: string;
  ctaExternalUrl?: string;
  slideCount: number;
}

const SHOWCASES: ProductShowcase[] = [
  {
    key: 'screendeck',
    name: 'ScreenDeck',
    logoSrc: 'assets/icon/screendeck-wordmark.svg',
    tagKey: 'products.screendeckTag',
    descKey: 'products.screendeckDesc',
    featureKeys: [
      'products.showcase.screendeck.feature1',
      'products.showcase.screendeck.feature2',
      'products.showcase.screendeck.feature3',
      'products.showcase.screendeck.feature4',
    ],
    ctaLabelKey: 'products.showcase.screendeck.cta',
    ctaExternalUrl: 'https://www.screendeck.ch',
    slideCount: 3,
  },
  {
    key: 'chroniq',
    name: 'Chroniq',
    // Light-on-dark wordmark (artworks/Chroniq/logo_dark.png), like the other cards —
    // the round mark alone left this the only product here without its name in artwork.
    logoSrc: 'assets/icon/chroniq-title.png',
    tagKey: 'products.chroniqTag',
    descKey: 'products.chroniqDesc',
    featureKeys: [
      'products.showcase.chroniq.feature1',
      'products.showcase.chroniq.feature2',
      'products.showcase.chroniq.feature3',
    ],
    subItems: [
      { titleKey: 'products.showcase.chroniq.mode1Title', descKey: 'products.showcase.chroniq.mode1Desc' },
      { titleKey: 'products.showcase.chroniq.mode2Title', descKey: 'products.showcase.chroniq.mode2Desc' },
      { titleKey: 'products.showcase.chroniq.mode3Title', descKey: 'products.showcase.chroniq.mode3Desc' },
    ],
    privacyKey: 'products.showcase.chroniq.privacy',
    privacyProduct: 'chroniq',
    ctaLabelKey: 'products.showcase.chroniq.cta',
    ctaRoute: '/contact',
    slideCount: 3,
  },
  {
    key: 'tablestack',
    name: 'TableStack',
    logoSrc: 'assets/icon/tablestack.png',
    tagKey: 'products.tablestackTag',
    descKey: 'products.tablestackDesc',
    featureKeys: [
      'products.showcase.tablestack.feature1',
      'products.showcase.tablestack.feature2',
      'products.showcase.tablestack.feature3',
      'products.showcase.tablestack.feature4',
    ],
    privacyKey: 'products.showcase.tablestack.privacy',
    privacyProduct: 'tablestack',
    ctaLabelKey: 'products.showcase.tablestack.cta',
    ctaRoute: '/contact',
    slideCount: 3,
  },
  {
    key: 'menulink',
    name: 'MenuLink',
    logoSrc: 'assets/icon/menulink.png',
    tagKey: 'products.menulinkTag',
    descKey: 'products.menulinkDesc',
    featureKeys: [
      'products.showcase.menulink.feature1',
      'products.showcase.menulink.feature2',
      'products.showcase.menulink.feature3',
      'products.showcase.menulink.feature4',
    ],
    comingSoonFeatureKeys: [
      'products.showcase.menulink.feature5comingsoon',
    ],
    privacyKey: 'products.showcase.menulink.privacy',
    // Legal-page anchor stays 'menu': it is a public URL contract (#menu) that
    // already-printed MenuLink footers link to.
    privacyProduct: 'menu',
    ctaLabelKey: 'products.showcase.menulink.cta',
    ctaRoute: '/contact',
    slideCount: 3,
  },
];

@Component({
    selector: 'app-products',
    templateUrl: './products.page.html',
    styleUrls: ['./products.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PortalNavbarComponent, IonicModule, TooltipDirective, RouterLink, PortalFooterComponent, TranslatePipe]
})
export class ProductsPage implements OnInit, OnDestroy {
  /** Drives the header variant: app-portal-navbar while signed out; signed in, the
   *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
  isAuthenticated = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private seo: SeoService,
    private readonly portalAuth: PortalAuthService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.seo.apply({ titleKey: 'products.seoTitle', descriptionKey: 'products.heroSub', path: '/products' });

    this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
      this.isAuthenticated = auth;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  readonly showcases = SHOWCASES;

  // Slide index per product, keyed by ProductShowcase.key — a plain map is enough
  // since each slider is only navigated via its own prev/next arrows and dots
  // (same trivial mechanism as the home page's product showcases, no auto-rotation).
  private readonly slideIndex: Record<string, number> = Object.fromEntries(SHOWCASES.map(s => [s.key, 0]));

  slideOf(key: string): number {
    return this.slideIndex[key] ?? 0;
  }

  setSlide(key: string, index: number): void {
    this.slideIndex[key] = index;
  }

  prevSlide(key: string, count: number): void {
    this.slideIndex[key] = (this.slideOf(key) - 1 + count) % count;
  }

  nextSlide(key: string, count: number): void {
    this.slideIndex[key] = (this.slideOf(key) + 1) % count;
  }

  slideNumbers(count: number): number[] {
    return Array.from({ length: count }, (_, i) => i);
  }

  portalFeatures = [
    { icon: 'people-outline', title: 'Mitarbeiterverwaltung', desc: 'Rollen, Positionen & Profile zentral verwalten' },
    { icon: 'receipt-outline', title: 'Swiss QR-Rechnungen', desc: 'Konforme QR-Bills erstellen & direkt versenden' },
    { icon: 'hardware-chip-outline', title: 'Gateway-Management', desc: 'Netzwerkgeräte in Echtzeit überwachen' },
    { icon: 'chatbubbles-outline', title: 'Support-Ticketing', desc: 'Integriertes Helpdesk mit Echtzeit-Chat' },
    { icon: 'shield-checkmark-outline', title: 'Auth0 Enterprise', desc: 'Sichere SSO-Authentifizierung mit Org-Support' },
    { icon: 'notifications-outline', title: 'SignalR Push', desc: 'Sofort-Benachrichtigungen via Azure SignalR' },
  ];
}
